import { z } from 'zod';

// Session mode is the user's default preference for future runs.
// It must never be "plan" because plan mode is intended to be one-off and should auto-exit.
export const assistantSessionModeSchema = z.enum(['propose', 'execute']);
export type AssistantSessionMode = z.infer<typeof assistantSessionModeSchema>;

// Run mode is the per-message behavior.
export const assistantRunModeSchema = z.enum(['plan', 'propose', 'execute']);
export type AssistantRunMode = z.infer<typeof assistantRunModeSchema>;

export const assistantReasoningEffortSchema = z.enum([
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh'
]);
export type AssistantReasoningEffort = z.infer<typeof assistantReasoningEffortSchema>;

// Legacy enum (kept for migrations / compatibility reads).
export const assistantLegacyModeSchema = z.enum(['plan', 'apply', 'propose', 'execute']);
export type AssistantLegacyMode = z.infer<typeof assistantLegacyModeSchema>;

export const normalizeAssistantSessionMode = (value: unknown): AssistantSessionMode | null => {
  if (value === 'plan') return 'propose';
  if (value === 'apply') return 'execute';
  if (value === 'propose' || value === 'execute') return value;
  return null;
};

export const normalizeAssistantRunMode = (value: unknown): AssistantRunMode | null => {
  if (value === 'apply') return 'execute';
  if (value === 'plan' || value === 'propose' || value === 'execute') return value;
  return null;
};

// Back-compat: many call sites still import AssistantMode/assistantModeSchema.
// Treat it as the run-mode union (plan/propose/execute). Prefer AssistantRunMode going forward.
export const assistantModeSchema = assistantRunModeSchema;
export type AssistantMode = AssistantRunMode;

export const assistantRunStatusSchema = z.enum(['queued', 'running', 'completed', 'failed', 'canceled']);
export type AssistantRunStatus = z.infer<typeof assistantRunStatusSchema>;

export const assistantMessageRoleSchema = z.enum(['user', 'assistant', 'tool', 'system']);
export type AssistantMessageRole = z.infer<typeof assistantMessageRoleSchema>;

export const assistantAssetKindSchema = z.enum([
  'image',
  'video',
  'audio',
  'pdf',
  'ppt',
  'text',
  'unknown'
]);
export type AssistantAssetKind = z.infer<typeof assistantAssetKindSchema>;

export const assistantAssetSourceSchema = z.enum([
  'upload',
  'generated',
  'reference',
  'studio',
  'external'
]);
export type AssistantAssetSource = z.infer<typeof assistantAssetSourceSchema>;

export const assistantActionStatusSchema = z.enum(['proposed', 'applied', 'rejected']);
export type AssistantActionStatus = z.infer<typeof assistantActionStatusSchema>;

export const assistantSessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  title: z.string().optional(),
  mode: assistantSessionModeSchema,
  agentProvider: z.enum(['openai', 'claude']).optional(),
  agentSessionId: z.string().optional(),
  agentLastResponseId: z.string().optional(),
  agentLastInputTokens: z.number().optional(),
  agentSessionItems: z.array(z.record(z.string(), z.unknown())).optional(),
  agentMemorySummary: z.string().optional(),
  agentCompactionItems: z.array(z.record(z.string(), z.unknown())).optional(),
  lastActiveAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type AssistantSession = z.infer<typeof assistantSessionSchema>;

export const assistantRunSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  userId: z.string(),
  clientMessageId: z.string().optional(),
  mode: assistantRunModeSchema,
  agentProvider: z.enum(['openai', 'claude']).optional(),
  status: assistantRunStatusSchema,
  userMessageId: z.string(),
  assistantMessageId: z.string().optional(),
  agentSessionId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  error: z.string().optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type AssistantRun = z.infer<typeof assistantRunSchema>;

export const assistantMessageSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  clientMessageId: z.string().optional(),
  role: assistantMessageRoleSchema,
  content: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  toolCalls: z.array(z.record(z.string(), z.unknown())).optional(),
  toolResults: z.array(z.record(z.string(), z.unknown())).optional(),
  createdAt: z.string()
});
export type AssistantMessage = z.infer<typeof assistantMessageSchema>;

export const assistantAssetSchema = z.object({
  id: z.string(),
  userId: z.string(),
  sessionId: z.string(),
  source: assistantAssetSourceSchema,
  kind: assistantAssetKindSchema,
  filename: z.string().optional(),
  mimeType: z.string().optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  assetUrl: z.string().optional(),
  status: z.string().optional(),
  previews: z.array(z.record(z.string(), z.unknown())).optional(),
  textExtract: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string()
});
export type AssistantAsset = z.infer<typeof assistantAssetSchema>;

export const assistantAssetLabelSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  assetId: z.string(),
  label: z.string(),
  createdAt: z.string()
});
export type AssistantAssetLabel = z.infer<typeof assistantAssetLabelSchema>;

export const assistantActionSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  status: assistantActionStatusSchema,
  diff: z.record(z.string(), z.unknown()).optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  result: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string()
});
export type AssistantAction = z.infer<typeof assistantActionSchema>;

export const assistantEventVisibilitySchema = z.enum(['user', 'debug']);
export type AssistantEventVisibility = z.infer<typeof assistantEventVisibilitySchema>;

export const assistantEventTypeSchema = z.enum([
  'status',
  'response.delta',
  'response.final',
  'action.summary',
  'moderation',
  'error',
  'tool.call',
  'tool.result'
]);
export type AssistantEventType = z.infer<typeof assistantEventTypeSchema>;

export const assistantEventSchema = z.object({
  id: z.string(),
  runId: z.string(),
  sessionId: z.string(),
  sequence: z.number().int().nonnegative(),
  type: assistantEventTypeSchema,
  visibility: assistantEventVisibilitySchema,
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string()
});
export type AssistantEvent = z.infer<typeof assistantEventSchema>;

export const assistantContinuationStatusSchema = z.enum([
  'pending',
  'running',
  'completed',
  'failed',
  'canceled'
]);
export type AssistantContinuationStatus = z.infer<typeof assistantContinuationStatusSchema>;

export const assistantContinuationSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  userId: z.string(),
  runId: z.string().optional(),
  actionId: z.string().optional(),
  jobId: z.string(),
  status: assistantContinuationStatusSchema,
  objective: z.string().optional(),
  attempts: z.number().int().nonnegative().optional(),
  lastError: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
});
export type AssistantContinuation = z.infer<typeof assistantContinuationSchema>;

export const assistantArtifactSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  type: z.string(),
  version: z.string().optional(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string()
});
export type AssistantArtifact = z.infer<typeof assistantArtifactSchema>;

export const assistantShortsSegmentSchema = z.object({
  startSec: z.number().nonnegative(),
  endSec: z.number().positive(),
  reason: z.string().optional()
});
export type AssistantShortsSegment = z.infer<typeof assistantShortsSegmentSchema>;

export const assistantShortsCropSchema = z.object({
  startSec: z.number().nonnegative(),
  endSec: z.number().positive(),
  x: z.number().nonnegative(),
  y: z.number().nonnegative(),
  width: z.number().positive(),
  height: z.number().positive(),
  speakerId: z.string().optional(),
  confidence: z.number().optional()
});
export type AssistantShortsCrop = z.infer<typeof assistantShortsCropSchema>;

export const assistantShortsOutputSchema = z.object({
  assetId: z.string(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  startSec: z.number().nonnegative().optional(),
  endSec: z.number().positive().optional()
});
export type AssistantShortsOutput = z.infer<typeof assistantShortsOutputSchema>;

export const assistantShortsSummarySchema = z.object({
  status: z.enum(['queued', 'running', 'succeeded', 'failed', 'partial']).optional(),
  sourceAssetId: z.string().optional(),
  durationSeconds: z.number().optional(),
  highlights: z.array(assistantShortsSegmentSchema).optional(),
  cropPlan: z.array(assistantShortsCropSchema).optional(),
  cropEngine: z.enum(['gemini', 'clipsai', 'clipsai-segment']).optional(),
  outputs: z.array(assistantShortsOutputSchema).optional(),
  failures: z
    .array(
      z.object({
        index: z.number().nonnegative(),
        reason: z.string()
      })
    )
    .optional(),
  error: z.string().optional(),
  errorCode: z.string().optional()
});
export type AssistantShortsSummary = z.infer<typeof assistantShortsSummarySchema>;
